import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Collapsible explainer panel for the cheat-detection system.
 *
 * Documents three things in plain Swedish so reviewers know what they're
 * looking at:
 *
 *  1. The investigation flow (match → analysis → assignment → review).
 *  2. The score legend with the configurable review/notify thresholds
 *     pulled live from the backend (so the bands always match the
 *     thresholds the API is actually filtering on).
 *  3. Each detection flag with its weight and what it actually measures.
 *     The weights mirror ``FLAG_WEIGHTS`` in the API's
 *     ``players/demo_cheat_analysis.py`` — update both together.
 *
 * Renders as a collapsed `<details>` block by default to keep the
 * dashboard chrome quiet on repeat visits.
 *
 * @param {object} props
 * @param {number} [props.reviewThreshold=3] - Score at/above which a report
 *   shows up in the dashboard by default.
 * @param {number} [props.notifyThreshold=6] - Score at/above which a report
 *   counts as a strong-suspicion notification.
 */
export default function CheatDetectionInfo({ reviewThreshold = 3, notifyThreshold = 6 }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const flowSteps = [
    { title: t('overwatch.cdFlow1Title'), body: t('overwatch.cdFlow1Body') },
    { title: t('overwatch.cdFlow2Title'), body: t('overwatch.cdFlow2Body') },
    { title: t('overwatch.cdFlow3Title'), body: t('overwatch.cdFlow3Body') },
    { title: t('overwatch.cdFlow4Title'), body: t('overwatch.cdFlow4Body') },
    { title: t('overwatch.cdFlow5Title'), body: t('overwatch.cdFlow5Body', { threshold: reviewThreshold.toFixed(1) }) },
    { title: t('overwatch.cdFlow6Title'), body: t('overwatch.cdFlow6Body') },
    { title: t('overwatch.cdFlow7Title'), body: t('overwatch.cdFlow7Body') },
  ];

  const scoreBands = [
    { tier: 'clean', label: t('overwatch.cdBandCleanLabel'), range: `0 – ${reviewThreshold.toFixed(1)}`, blurb: t('overwatch.cdBandCleanBlurb') },
    { tier: 'watch', label: t('overwatch.cdBandWatchLabel'), range: `${reviewThreshold.toFixed(1)} – ${notifyThreshold.toFixed(1)}`, blurb: t('overwatch.cdBandWatchBlurb') },
    { tier: 'sus', label: t('overwatch.cdBandSusLabel'), range: `${notifyThreshold.toFixed(1)} – 8.0`, blurb: t('overwatch.cdBandSusBlurb') },
    { tier: 'cheat', label: t('overwatch.cdBandCheatLabel'), range: '8.0 – 10', blurb: t('overwatch.cdBandCheatBlurb') },
  ];

  const statFlags = [
    { name: 'headshot_anomaly', weight: 1.0, desc: t('overwatch.cdStatHeadshot') },
    { name: 'kd_anomaly', weight: 0.5, desc: t('overwatch.cdStatKd') },
    { name: 'adr_anomaly', weight: 0.5, desc: t('overwatch.cdStatAdr') },
    { name: 'opening_kill_dominance', weight: 1.0, desc: t('overwatch.cdStatOpening') },
    { name: 'multikill_anomaly', weight: 0.5, desc: t('overwatch.cdStatMultikill') },
    { name: 'duel_dominance', weight: 1.5, desc: t('overwatch.cdStatDuel') },
  ];

  const demoFlags = [
    { name: 'snap_anomaly', weight: 0.0, desc: t('overwatch.cdDemoSnap') },
    { name: 'crosshair_accuracy', weight: 0.0, desc: t('overwatch.cdDemoCrosshair') },
    { name: 'low_aim_variance', weight: 0.0, desc: t('overwatch.cdDemoVariance') },
    { name: 'smoke_shoot', weight: 3.0, desc: t('overwatch.cdDemoSmoke') },
    { name: 'wallhack_kill', weight: 6.0, desc: t('overwatch.cdDemoWallhack') },
    { name: 'fast_reaction', weight: 4.0, desc: t('overwatch.cdDemoReaction') },
    { name: 'prefire', weight: 0.0, desc: t('overwatch.cdDemoPrefire') },
    { name: 'weapon_hs_anomaly', weight: 2.0, desc: t('overwatch.cdDemoWeaponHs') },
    { name: 'wall_tracking', weight: 2.0, desc: t('overwatch.cdDemoWallTracking') },
  ];

  return (
    <details className="cd-info" open={open} onToggle={(e) => setOpen(e.target.open)}>
      <summary className="cd-info-summary">
        <span className="cd-info-icon">📖</span>
        <span>{t('overwatch.cdSummary')}</span>
      </summary>

      <div className="cd-info-body">
        <section className="cd-info-section">
          <h3>{t('overwatch.cdFlowHeading')}</h3>
          <ol className="cd-flow">
            {flowSteps.map((step, idx) => (
              <li key={step.title} className="cd-flow-step">
                <div className="cd-flow-num">{idx + 1}</div>
                <div className="cd-flow-content">
                  <div className="cd-flow-title">{step.title}</div>
                  <div className="cd-flow-body">{step.body}</div>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="cd-info-section">
          <h3>{t('overwatch.cdScoreHeading')}</h3>
          <p className="cd-info-blurb">
            {t('overwatch.cdScoreBlurb')}
          </p>
          <div className="cd-score-bands">
            {scoreBands.map((band) => (
              <div key={band.tier} className={`cd-score-band tier-${band.tier}`}>
                <div className="cd-score-band-range">{band.range}</div>
                <div className="cd-score-band-label">{band.label}</div>
                <div className="cd-score-band-blurb">{band.blurb}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="cd-info-section">
          <h3>{t('overwatch.cdStatHeading')}</h3>
          <p className="cd-info-blurb">
            {t('overwatch.cdStatBlurb')}
          </p>
          <table className="cd-flag-table">
            <thead>
              <tr><th>{t('overwatch.cdColFlag')}</th><th>{t('overwatch.cdColWeight')}</th><th>{t('overwatch.cdColCatches')}</th></tr>
            </thead>
            <tbody>
              {statFlags.map((f) => (
                <tr key={f.name}>
                  <td><code>{f.name}</code></td>
                  <td>{f.weight.toFixed(1)}</td>
                  <td>{f.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="cd-info-section">
          <h3>{t('overwatch.cdDemoHeading')}</h3>
          <p className="cd-info-blurb">
            {t('overwatch.cdDemoBlurb')}
          </p>
          <table className="cd-flag-table">
            <thead>
              <tr><th>{t('overwatch.cdColFlag')}</th><th>{t('overwatch.cdColWeight')}</th><th>{t('overwatch.cdColCatches')}</th></tr>
            </thead>
            <tbody>
              {demoFlags.map((f) => (
                <tr key={f.name}>
                  <td><code>{f.name}</code></td>
                  <td>{f.weight.toFixed(1)}</td>
                  <td>{f.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="cd-info-blurb">
            {t('overwatch.cdDemoZeroNote')}
          </p>
        </section>
      </div>
    </details>
  );
}
